import { useCallback } from "react";

type GetSignUpUrl = (opts: { state: { returnTo: string } }) => Promise<string>;
type SignUp = (opts: { state: { returnTo: string } }) => Promise<void>;

/**
 * One-click GitHub sign-up for the public-workspace lurker flow. AuthKit
 * exposes no provider hint, so we build the WorkOS sign-up URL and OVERWRITE
 * its provider with the GitHub connection. `getSignUpUrl` already sets
 * `provider=authkit` (+ `screen_hint=sign-up`); appending `&provider=GitHubOAuth`
 * would leave a duplicate param whose FIRST value (`authkit`) WorkOS honors —
 * sending the user to the hosted page, not GitHub. So we `set()` the provider
 * (replaces the dup) and drop `screen_hint` (only valid for `authkit`).
 * Stashes the return URL (same key AuthGate uses) and falls back to the hosted
 * sign-up page (where GitHub is selectable) if the URL build fails. Navigates
 * away immediately, so no in-progress state to manage.
 */
export function useGithubSignup(
  getSignUpUrl: GetSignUpUrl,
  signUp: SignUp,
  trackIntent: () => void,
): () => Promise<void> {
  return useCallback(async () => {
    trackIntent();
    const returnTo = window.location.href;
    try {
      sessionStorage.setItem("ano:auth-return-url", returnTo);
    } catch {
      /* storage disabled — returnTo still rides the WorkOS state param */
    }
    try {
      const url = new URL(await getSignUpUrl({ state: { returnTo } }));
      // `set` REPLACES the `provider=authkit` getSignUpUrl baked in (vs append,
      // which leaves a first-wins duplicate). Drop `screen_hint` — WorkOS only
      // accepts it for the `authkit` provider.
      url.searchParams.set("provider", "GitHubOAuth");
      url.searchParams.delete("screen_hint");
      window.location.assign(url.toString());
    } catch (error) {
      console.error("Failed to start GitHub sign up:", error);
      void signUp({ state: { returnTo } });
    }
  }, [getSignUpUrl, signUp, trackIntent]);
}
